import CollectionDynamic from "./CollectionDynamic";
import Datasource, { DatasourceResponse } from "lib/datasource";
import moment from "moment";
import React, { memo, useCallback, useContext, useEffect, useState } from "react";
import { Breadcrumbs, Header, Workspace } from "components/Shared";
import { RouteComponentProps } from "react-router-dom";
import { isFeatureToggled, translate } from "lib";
import { UserContext } from "context";

/**
 * THE "templates" FLAG GATES THE SECTION, NOT THE BUTTONS (Sim, 22 Sep 2026).
 *
 * It used to hide only the two "New … Workflow Template" toolbar buttons, so
 * anyone could still see the Templates nav entry, open the list and edit an
 * existing template. The flag now controls the nav entry (see both Mastheads)
 * and this screen refuses a direct URL — which makes the button-level check
 * redundant: nobody without the flag gets this far, so the toolbar is
 * unconditional again.
 */
const Templates = ({ history }: any) => {
	const { currentUser } = useContext(UserContext) as { currentUser: tcpinpoint.User };
	// Hiding the nav entry is not access control — the URL is still typable, and
	// an old bookmark or link still resolves. `replace`, not `push`, so Back off
	// the dashboard does not bounce straight back here.
	const canAccessTemplates = !!isFeatureToggled("templates", currentUser);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "template", renderTrigger: renderTrigger }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success) history.push(`/templates/${response.normalizedMainModelResponse.id}`);
		});
		return () => sub.unsubscribe();
	}, [datasource, history]);

	useEffect(() => {
		if (!canAccessTemplates) history.replace("/");
	}, [canAccessTemplates, history]);

	const createTemplate = useCallback(
		(kind: string) => {
			datasource.post("v2", `templates`, { body: { template: { kind } } }, { updateIsLoding: true });
		},
		[datasource]
	);

	// Hold the redirect rather than painting the list for one frame first —
	// effects run AFTER the render that scheduled them.
	if (!canAccessTemplates) return null;

	return (
		<React.Fragment>
      <Breadcrumbs items={[{ title: "templates" }]} />
			<Header
				title={translate("list_templates")}
				stamp={"templates"}
				toolbar={[
					{
						label: "start_new_retail_workflow_template",
						icon: "new",
						onClick: () => createTemplate("standard-retail-workflow")
					},
					{
						label: "start_new_commercial_workflow_template",
						icon: "new",
						onClick: () => createTemplate("standard-commercial-workflow")
					}
				]}
			/>
			<Workspace id="templates" className="list">
				<CollectionDynamic />
			</Workspace>
		</React.Fragment>
	);
};

const MemoizedTemplates = memo(Templates);
export default ({ history }: RouteComponentProps) => <MemoizedTemplates history={history} />;
